class PriveVideoSlider {
  constructor(root) {
    this.root = root;
    this.sliderEl = root.querySelector('[data-prive-video-slider]');
    this.prevBtn = root.querySelector('[data-prive-video-prev]');
    this.nextBtn = root.querySelector('[data-prive-video-next]');

    if (!this.sliderEl) return;

    this.slideCount = Number(root.dataset.slideCount || 0);
    this.enableLoop = this.slideCount > 3;
    this.isMobile = window.matchMedia('(max-width: 989px)').matches;
    this.initialSlide = this.isMobile && this.slideCount >= 2 ? 1 : 0;

    this.prepareLazyVideos();

    this.swiper = new Swiper(this.sliderEl, {
      slidesPerView: 'auto',
      centeredSlides: true,
      spaceBetween: 10,
      speed: 500,
      initialSlide: this.initialSlide,
      loop: this.enableLoop,
      loopAdditionalSlides: 2,
      watchOverflow: true,
      watchSlidesProgress: true,
      slideToClickedSlide: true,
      navigation: {
        prevEl: this.prevBtn,
        nextEl: this.nextBtn,
      },
      breakpoints: {
        750: {
          slidesPerView: 2.2,
          spaceBetween: 12,
          centeredSlides: true,
        },
        990: {
          slidesPerView: 3.15,
          spaceBetween: 14,
          centeredSlides: false,
        },
      },
      on: {
        init: () => {
          if (!this.isMobile) {
            this.swiper.slideTo(0, 0);
          }
          this.syncVideoPlayback();
        },
        slideChange: () => {
          this.syncVideoPlayback();
        },
        progress: () => {
          this.syncVideoPlayback();
        },
      },
    });

    this.bindControls();
    this.bindAddToCart();
    this.bindFullscreenEvents();
    this.bindResponsiveSlide();
  }

  bindAddToCart() {
    this.root.querySelectorAll('.prive-video-slider__atc').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
      });
    });
  }

  bindResponsiveSlide() {
    const desktopQuery = window.matchMedia('(min-width: 990px)');

    const handleBreakpointChange = (event) => {
      if (!this.swiper || this.slideCount < 2) return;

      if (event.matches) {
        this.swiper.slideTo(0, 0);
        return;
      }

      this.swiper.slideTo(1, 0);
    };

    if (typeof desktopQuery.addEventListener === 'function') {
      desktopQuery.addEventListener('change', handleBreakpointChange);
    } else if (typeof desktopQuery.addListener === 'function') {
      desktopQuery.addListener(handleBreakpointChange);
    }
  }

  bindControls() {
    this.root.querySelectorAll('[data-prive-video-mute]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const media = button.closest('[data-prive-video-media]');
        const video = media?.querySelector('video');
        if (!video) return;

        video.muted = !video.muted;
        this.updateMuteButton(button, video.muted);
      });
    });

    this.root.querySelectorAll('[data-prive-video-fullscreen]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const media = button.closest('[data-prive-video-media]');
        if (!media) return;

        this.toggleFullscreen(media, button);
      });
    });

    this.root.querySelectorAll('[data-prive-video-media]').forEach((media) => {
      const muteButton = media.querySelector('[data-prive-video-mute]');
      const video = media.querySelector('video');
      if (muteButton && video) {
        this.updateMuteButton(muteButton, video.muted);
      }
    });
  }

  bindFullscreenEvents() {
    document.addEventListener('fullscreenchange', () => {
      this.root.querySelectorAll('[data-prive-video-media]').forEach((media) => {
        const fullscreenButton = media.querySelector('[data-prive-video-fullscreen]');
        const isFullscreen = document.fullscreenElement === media;
        media.classList.toggle('is-fullscreen', isFullscreen);
        if (fullscreenButton) {
          this.updateFullscreenButton(fullscreenButton, isFullscreen);
        }
      });
    });
  }

  prepareLazyVideos() {
    this.root.querySelectorAll('[data-prive-video-media] video').forEach((video) => {
      video.preload = 'none';
      video.removeAttribute('autoplay');
      video.querySelectorAll('source').forEach((source) => {
        if (source.src && !source.dataset.src) {
          source.dataset.src = source.getAttribute('src');
        }
        source.removeAttribute('src');
      });
      if (video.src && !video.dataset.src) {
        video.dataset.src = video.getAttribute('src');
        video.removeAttribute('src');
      }
      try {
        video.load();
      } catch (e) {
        /* ignore */
      }
    });
  }

  activateVideo(video) {
    if (!video) return;

    video.querySelectorAll('source').forEach((source) => {
      if (!source.getAttribute('src') && source.dataset.src) {
        source.setAttribute('src', source.dataset.src);
      }
    });

    if (!video.getAttribute('src') && video.dataset.src) {
      video.setAttribute('src', video.dataset.src);
    }

    if (video.dataset.priveLoaded !== 'true') {
      video.preload = 'metadata';
      try {
        video.load();
      } catch (e) {
        /* ignore */
      }
      video.dataset.priveLoaded = 'true';
    }

    video.muted = true;
    video.play().catch(() => {});
  }

  syncVideoPlayback() {
    this.root.querySelectorAll('[data-prive-video-media]').forEach((media) => {
      const video = media.querySelector('video');
      const slide = media.closest('.swiper-slide');
      if (!video || !slide) return;

      const isVisible =
        slide.classList.contains('swiper-slide-visible') ||
        slide.classList.contains('swiper-slide-active');

      if (isVisible) {
        this.activateVideo(video);
      } else {
        video.pause();
        video.muted = true;
        const muteButton = media.querySelector('[data-prive-video-mute]');
        if (muteButton) this.updateMuteButton(muteButton, true);
      }
    });
  }

  async toggleFullscreen(media, button) {
    const video = media.querySelector('video');

    try {
      if (document.fullscreenElement === media) {
        await document.exitFullscreen();
        return;
      }

      if (media.requestFullscreen) {
        await media.requestFullscreen();
      } else if (video?.webkitEnterFullscreen) {
        video.webkitEnterFullscreen();
      }
    } catch (error) {
      if (video?.webkitEnterFullscreen) {
        video.webkitEnterFullscreen();
      }
    }

    this.updateFullscreenButton(button, document.fullscreenElement === media);
  }

  updateMuteButton(button, isMuted) {
    button.classList.toggle('is-unmuted', !isMuted);
    button.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
  }

  updateFullscreenButton(button, isFullscreen) {
    button.classList.toggle('is-fullscreen', isFullscreen);
    button.setAttribute('aria-label', isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (typeof Swiper === 'undefined') return;

  document.querySelectorAll('[data-prive-video-slider-root]').forEach((root) => {
    new PriveVideoSlider(root);
  });
});
